import React, { useCallback, useRef, useState } from 'react';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { ContentContainerType } from 'types/enums';
import { useContextMenu } from 'app/components/ChakraTableList';
import { VersionCreditsListEmptyState } from 'app/components/Releases/VersionCreditsListEmptyState';
import {
  GroupedVersionCredits,
  ReleaseType,
  ReleaseVersionContribution,
} from 'types/releases';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { SlimBanner } from 'app/components/SlimBanner';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { useCreditedHandler, useListColumns } from './hooks';
import { getSubheaderProps } from './utils';
import { VersionCreditsListComponentProps } from './types';
import { ContributionRow } from './ContributionRow';
import { ReleaseActions } from '../ReleaseActions';

export const VersionCreditsListComponent: React.FC<VersionCreditsListComponentProps> = ({
  credits,
  rolesDDL = {},
  syncCredits,
  syncState,
  updateCreditsList,
  loading,
  releaseType,
  validationErrors,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { onCreditedChange, isUpdating } = useCreditedHandler({
    updateCreditsList,
  });

  const handleSync = async () => {
    setIsOpen(false);
    await syncCredits();
  };

  const tableKey =
    releaseType === ReleaseType.LABEL
      ? 'release-label-credits'
      : 'release-credits';
  const ref = useRef<HTMLDivElement>(null);
  const contextMenuProps = useContextMenu();

  const totalWidth = useTotalWidth(ref);
  const columns = useListColumns({
    credits,
    releaseType,
    totalWidth,
  });

  const rowRenderer = useCallback(
    (row: Row<GroupedVersionCredits>) => {
      const contribution = (row.original as unknown) as ReleaseVersionContribution;

      const {
        menuOptions,
        roleColumn,
        addedByColumn,
        date,
        location,
      } = getSubheaderProps({
        contribution,
        rolesDDL,
        updateCreditsList,
      });

      return (
        <ContributionRow
          key={row.id}
          row={row}
          validationErrors={validationErrors}
          onCreditedChange={onCreditedChange}
          isUpdating={isUpdating}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, menuOptions)
          }
          releaseType={releaseType}
          location={location}
          roleColumn={roleColumn}
          addedByColumn={addedByColumn}
          date={date}
        />
      );
    },
    [
      contextMenuProps,
      isUpdating,
      onCreditedChange,
      releaseType,
      rolesDDL,
      updateCreditsList,
      validationErrors,
    ]
  );

  if (!loading && credits.length === 0) {
    return (
      <VersionCreditsListEmptyState
        syncState={syncState}
        handleSync={handleSync}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        releaseType={releaseType}
      />
    );
  }

  const missingContribution = validationErrors?.some(
    ({ errorCode }) => errorCode === 'MISSING_NON_SONGWRITER_CONTRIBUTION'
  );

  return (
    <ContentContainer type={ContentContainerType.LIST} pb={24}>
      <ReleaseActions tab="contributions" syncState={syncState} />
      {missingContribution && (
        <SlimBanner
          variant="warning"
          primaryText="The Song need to have at least one non-songwriter credit."
          mb={6}
        />
      )}
      <StickyTable<GroupedVersionCredits>
        tableKey={tableKey}
        ref={ref}
        data={credits}
        columns={columns}
        rowRenderer={rowRenderer}
        loading={loading || syncState}
        contextMenuProps={contextMenuProps}
        getSubRows={(row: any) => row?.contributions || []}
      />
    </ContentContainer>
  );
};
